import {
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { CustomValidators } from '@nimmsta/ng-commons-lib/utils/custom-validators';
import {
  defaultToolbar,
  EditorComponent,
} from '@nimmsta/ng-commons-lib/editor';
import {
  SaveableFormComponent,
  InputWithErrorComponent,
} from '@nimmsta/ng-commons-lib/forms';
import { User } from '@nimmsta/ng-commons-lib/models';
import { UserService } from '@nimmsta/ng-commons-lib/services/user';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { switchMap } from 'rxjs';
import hljs from 'highlight.js';
import { WorkflowEmail } from './workflow-email';
import { WorkflowEmailService } from './workflow-email.service';
import { EmailUser } from './email-user';
import { TranslatePipe } from '@ngx-translate/core';
import { MultiSelect } from 'primeng/multiselect';
import { PrimeTemplate } from 'primeng/api';
import { NgClass, NgIf } from '@angular/common';

@Component({
  selector: 'app-email-form',
  templateUrl: 'email-form.component.html',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    MultiSelect,
    PrimeTemplate,
    NgClass,
    NgIf,
    TranslatePipe,
    InputWithErrorComponent,
    ErrorComponent,
    EditorComponent,
  ],
})
export class EmailFormComponent
  extends SaveableFormComponent<WorkflowEmail>
  implements OnInit
{
  private fb = inject(FormBuilder);
  private userService = inject(UserService);

  protected override dataService = inject(WorkflowEmailService);

  @Output() override closeEvent = new EventEmitter();

  @Output() override loadingEvent = new EventEmitter<boolean>();

  @ViewChild('customMail', { static: false }) customMail!: ElementRef;

  users: EmailUser[] = [];

  private loadedUsers: User[] = [];

  isEmailValid = true;

  emailSentSuccessfully = false;

  code = '';

  modules = {
    syntax: { hljs },
    toolbar: defaultToolbar,
  };

  TEMPLATE_KEYWORDS: { [key: string]: boolean } = {
    range: true,
    if: true,
    else: true,
    end: true,
    with: true,
    define: true,
    block: true,
    template: true,
    nil: true,
    true: true,
    false: true,
  };

  constructor() {
    super();
    this.setValue({} as WorkflowEmail);
  }

  ngOnInit(): void {
    this.userService
      .list({
        filter: null,
        sortColumn: 'family_name',
        pageSize: 9999,
      })
      .subscribe((users) => {
        this.loadedUsers = users.content;
      });
  }

  protected override setFormGroup(): void {
    this.code = this.value.htmlContent;
    this.form = this.fb.group({
      id: this.value.id,
      title: [this.value.subject, [Validators.required]],
      identifier: [
        this.value.identifier,
        [
          Validators.required,
          CustomValidators.patternValidator(/^[a-zA-Z0-9-]*$/, {
            noSpaceOrSpecial: true,
          }),
        ],
      ],
      subject: [this.value.subject, [Validators.required]],
      htmlContent: [this.value.htmlContent, [Validators.required]],
      allRecipients: [undefined, [Validators.required]],
    });
  }

  override applyCustomValues(): void {
    this.value.recipientUserIds = this.form
      .get('allRecipients')!
      .value.filter((user: EmailUser) => user.id !== undefined)
      .map((user: EmailUser) => user.id!);
    this.value.recipients = this.form
      .get('allRecipients')!
      .value.filter((user: EmailUser) => user.email !== undefined)
      .map((user: EmailUser) => user.email!);

    // Convert template keywords to golang template syntax
    let htmlContent = this.value.htmlContent;
    htmlContent = htmlContent.replace(/&nbsp;/g, ' ');
    htmlContent = htmlContent.replace(/{{\s*([^}\s]+)\s*}}/g, (match, p1) => {
      if (p1[0] === '.' || p1[0] === '$' || this.TEMPLATE_KEYWORDS[p1]) {
        return match;
      }
      return `{{ .${p1} }}`;
    });
    // Replace all non-breaking spaces with regular spaces and multiple spaces with single spaces
    htmlContent = htmlContent.replace(/\u00A0/g, ' ');
    htmlContent = htmlContent.replace(/\s+/g, ' ').trim();
    this.value.htmlContent = htmlContent;
  }

  addCustomMail() {
    this.isEmailValid = true;
    const mail = this.customMail.nativeElement.value;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(mail)) {
      const newMail = {
        name: mail,
        email: mail,
      };
      this.customMail.nativeElement.value = '';
      this.users.push(newMail);
      this.form
        .get('allRecipients')!
        .setValue([...this.form.get('allRecipients')!.value, newMail]);
      this.form.markAsDirty();
    } else {
      this.isEmailValid = false;
    }
  }

  testEmail() {
    this.emailSentSuccessfully = false;
    if (this.form.valid) {
      this.loadingEvent.emit(true);
      this.value = Object.assign(this.value, this.form.value);
      this.value.htmlContent = this.code;
      this.applyCustomValues();
      this.form.disable();

      this.dataService
        .createOrUpdate(this.value)
        .pipe(switchMap((saved) => this.dataService.testEmail(saved)))
        .subscribe({
          next: () => {
            this.form.enable();
            this.form.markAsPristine();
            this.loadingEvent.emit(false);
            this.emailSentSuccessfully = true;
          },
          error: (response) => {
            this.error = response;
            this.form.enable();
            this.loadingEvent.emit(false);
          },
        });
    }
  }

  override setValue(email: WorkflowEmail) {
    super.setValue(email);
    this.emailSentSuccessfully = false;
    this.users = this.loadedUsers.map((user) => ({
      id: user.id,
      name: `${user.family_name} ${user.given_name}`,
    }));
    this.form.get('allRecipients')!.setValue([]);
    if (email) {
      if (email.recipientUserIds) {
        email.recipientUserIds.forEach((userId) => {
          const user = this.users.find((u) => u.id === userId);
          if (user) {
            this.form.get('allRecipients')!.value.push(user);
          }
        });
      }
      if (email.recipients) {
        email.recipients.forEach((customEmail) => {
          const customUser = {
            name: customEmail,
            email: customEmail,
          };
          if (!this.users.some((u) => u.email === customEmail)) {
            this.users.push(customUser);
          }
          this.form.get('allRecipients')!.value.push(customUser);
        });
      }
    }
    this.form.markAsPristine();
  }

  protected setHtmlContent(content: string | null) {
    this.form.controls['htmlContent'].setValue(content);
    this.form.controls['htmlContent'].markAsDirty();
  }
}
